import { getSearchMaterialFilter } from "@/services";
import { setSearch } from "@/tracker/shared/global";
import filterUrlParams from "@/utils/filterUrlParams";
import { useRequest } from "ahooks";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";

interface FilterOption {
  label: string;
  value: string;
  icon?: string;
  optionLabel?: string;
}

interface MaterialSourceFilter {
  name: string;
  options: FilterOption[];
  value: FilterOption | null;
  key: string;
}

/**
 * 素材来源筛选器 Hook
 * 用于相似素材页面等需要显示素材来源筛选器的地方
 *
 * @returns {Object} 返回筛选器配置和处理函数
 * @returns {MaterialSourceFilter | null} filter - 筛选器配置对象，如果数据未加载则返回 null
 * @returns {Function} onFilterChange - 筛选器值变化的处理函数
 * @returns {boolean} loading - 是否正在加载筛选器数据
 */
export function useMaterialSourceFilter() {
  const router = useRouter();
  const { filter_ids } = router.query;

  // 获取素材筛选器数据
  const [materialFilters, setMaterialFilters] = useState<any[]>([]);
  const { loading } = useRequest(getSearchMaterialFilter, {
    onSuccess: (filters: any) => {
      setMaterialFilters(filters || []);
    },
  });

  // 同步 filter_ids 到埋点的 searchData 中（供卡片曝光和点击使用）
  useEffect(() => {
    if (filter_ids) {
      try {
        const filterIdsMap = filterUrlParams.decode(filter_ids as string);
        const filterIdsArray = Object.values(filterIdsMap);
        setSearch({
          filter_ids: filterIdsArray,
        });
      } catch (e) {
        console.error("解析 filter_ids 失败", e);
      }
    } else {
      setSearch({
        filter_ids: [],
      });
    }
  }, [filter_ids]);

  // 筛选器配置
  const filter = useMemo((): MaterialSourceFilter | null => {
    const filterIdsMap = filter_ids
      ? filterUrlParams.decode(filter_ids as string)
      : {};

    // 从后端获取的筛选器中找到 source 筛选器（seo_id: "source"）
    const sourceFilter = materialFilters.find(
      (filter) => filter.key === "source"
    );

    if (!sourceFilter) {
      return null;
    }

    const currentValue = sourceFilter.options.find(
      (option: FilterOption) => filterIdsMap.material_source === option.value
    );

    return {
      name: sourceFilter.name || "全部素材",
      options: sourceFilter.options || [],
      value: currentValue || null,
      key: "material_source",
    };
  }, [filter_ids, materialFilters]);

  // 筛选器变化处理
  const onFilterChange = useCallback(
    (value: FilterOption | null) => {
      const filterIdsMap = filter_ids
        ? filterUrlParams.decode(filter_ids as string)
        : {};

      if (value?.value) {
        filterIdsMap.material_source = value.value;
      } else {
        delete filterIdsMap.material_source;
      }

      const query = { ...router.query };
      if (Object.keys(filterIdsMap).length > 0) {
        query.filter_ids = filterUrlParams.encode(filterIdsMap);
      } else {
        delete query.filter_ids;
      }

      router.push(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true }
      );
    },
    [filter_ids, router]
  );

  // 计算选择某个选项后的 filter_ids（用于埋点）
  const getFilterIdsForOption = useCallback(
    (optionValue: string | null) => {
      const filterIdsMap = filter_ids
        ? filterUrlParams.decode(filter_ids as string)
        : {};

      if (optionValue) {
        filterIdsMap.material_source = optionValue;
      } else {
        delete filterIdsMap.material_source;
      }

      return Object.values(filterIdsMap);
    },
    [filter_ids]
  );

  return {
    filter,
    onFilterChange,
    loading,
    getFilterIdsForOption,
  };
}
